import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { OrderService } from '../../../../services/order.service';
import saveAs from 'file-saver';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from 'primeng/api';
import { StorageService } from 'src/app/services/storage.service';
import {
  BulkOrderGroup,
  BulkFileDetail,
} from '../../automated-bulk-order/automated-bulk-order.component';
import { TableColumn } from 'src/app/shared-components/data-table/data-table.component';

@Component({
    selector: 'app-bulk-order-detail',
    templateUrl: './bulk-order-detail.component.html',
    styleUrls: ['./bulk-order-detail.component.scss'],
    standalone: false
})
export class BulkOrderDetailComponent implements OnInit, OnChanges {
  @Input() group: BulkOrderGroup | null = null;
  // Resolved via /user/details on the parent — storageService.getUserID()
  // is not the correct id to send to orders-report.
  @Input() userId: any = null;
  @Output() backClicked = new EventEmitter<void>();
  @ViewChild('viewOrderData') viewOrderData: TemplateRef<any>;

  readonly fileColumns: TableColumn[] = [
    { field: 's_no', header: 'S.No' },
    { field: 'file_id', header: 'File ID', sortable: true },
    { field: 'file_name', header: 'File Name', sortable: true },
    { field: 'referance_id', header: 'Reference ID', sortable: true },
    { field: 'order_id', header: 'Order ID', sortable: true, fallback: '—' },
    { field: 'status', header: 'Status', sortable: true },
    { field: 'error_log', header: 'Error Log' },
    { field: 'action', header: 'Action', toggleable: false },
  ];

  // ── File list ──────────────────────────────────────────────
  fileDetails: BulkFileDetail[] = [];
  isLoading: boolean = false;

  // ── Modal state ────────────────────────────────────────────
  viewOrderId: any = null;
  isBulkCatUploadOrder: boolean = false;

  // ── Sorting ────────────────────────────────────────────────
  currentSortColumn: string = '';
  sortDirection: string = 'asc';

  // ── Pagination ─────────────────────────────────────────────
  pageNo: number = 0;
  totalRecords: number = 0;
  rowsPerPage: any = 10;
  numberoftables: number;

  orderFilters: any = {
    page: 1,
    limit: 10,
    account_id: '',
    status: '',
  };

  constructor(
    private orderService: OrderService,
    private modalService: NgbModal,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private storageService: StorageService,
  ) {
    this.orderFilters['account_id'] = this.storageService.getAccountId() || null;
  }

  // ── Lifecycle ──────────────────────────────────────────────
  ngOnInit(): void {
    if (this.group?.bulk_order_uuid) {
      this.loadDetails();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const groupChanged =
      !!changes['group'] &&
      !!this.group?.bulk_order_uuid &&
      changes['group'].previousValue?.bulk_order_uuid !==
        changes['group'].currentValue?.bulk_order_uuid;
    const userIdResolved =
      !groupChanged &&
      !!changes['userId'] &&
      !changes['userId'].previousValue &&
      !!changes['userId'].currentValue &&
      !!this.group?.bulk_order_uuid;

    if (groupChanged) {
      this.orderFilters.page = 1;
      this.pageNo = 0;
      this.loadDetails();
    } else if (userIdResolved) {
      this.loadDetails();
    }
  }

  // ── API ────────────────────────────────────────────────────
  private _loadDetailsRequestId = 0;

  loadDetails(): void {
    this.isLoading = true;
    this.fileDetails = [];

    const payload = {
      bulk_order_uuid: this.group!.bulk_order_uuid,
      page: this.orderFilters.page || 1,
      limit: this.orderFilters.limit || 10,
      user_id: this.userId,
      status: this.orderFilters.status ? this.orderFilters.status : '',
      account_id: this.orderFilters.account_id,
    };

    const requestId = ++this._loadDetailsRequestId;

    this.orderService.getBulkCatUploadOrders(payload).subscribe(
      (resp) => {
        if (requestId !== this._loadDetailsRequestId) return;
        this.isLoading = false;
        const isSuccess = resp.status === 'success' || resp.status == 200;
        const data = resp.data ?? resp.content ?? [];

        if (isSuccess && Array.isArray(data) && data.length > 0) {
          const matched = data.find(
            (g: BulkOrderGroup) =>
              g.bulk_order_uuid === this.group!.bulk_order_uuid,
          );
          this.fileDetails = matched?.details ?? [];
          this.totalRecords =
            resp.pagination?.total_records ??
            matched?.total_files ??
            this.fileDetails.length;
          this.numberoftables = Math.ceil(this.totalRecords / this.rowsPerPage);
        } else {
          this.fileDetails = [];
          this.totalRecords = 0;
          this.numberoftables = 0;
        }
        this.cdr.detectChanges();
      },
      (err) => {
        if (requestId !== this._loadDetailsRequestId) return;
        this.isLoading = false;
        this.fileDetails = [];
        this.totalRecords = 0;
        this.cdr.detectChanges();
        console.error('[BulkOrderDetail] API error:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load file details.',
        });
      },
    );
  }

  // ── Pagination ─────────────────────────────────────────────
  onPageChange(event: any): void {
    this.orderFilters.page = event.page + 1;
    this.orderFilters.limit = event.rows;
    this.rowsPerPage = event.rows;
    this.pageNo = event.page * event.rows;
    this.loadDetails();
  }

  onRowsPerPageChange(event: any): void {
    this.rowsPerPage = event;
    this.orderFilters.limit = event;
    this.orderFilters.page = 1;
    this.pageNo = 0;
  }

  // ── Navigation ─────────────────────────────────────────────
  onBack(): void {
    this.backClicked.emit();
  }


  // ── Modal: normal order (eye icon) ─────────────────────────
  openViewOrderModal(file: BulkFileDetail): void {
    this.isBulkCatUploadOrder = false;
    this.viewOrderId = {
      orderId: file.order_id,
      bulkOrderid: null,
    };

    this.modalService
      .open(this.viewOrderData, {
        size: 'xl',
        ariaLabelledBy: 'modal-basic-title',
        backdrop: 'static',
        keyboard: false,
      })
      .result.finally(() => {
        // ← Refresh file statuses after modal closes
        this.loadDetails();
      });
  }

  // ── Modal: awaiting approval (folder icon) ─────────────────
  openViewandOrderModal(file: BulkFileDetail): void {
    this.isBulkCatUploadOrder = true;
    this.viewOrderId = file.order_id ?? file;

    this.modalService
      .open(this.viewOrderData, {
        size: 'xl',
        ariaLabelledBy: 'modal-basic-title',
        backdrop: 'static',
        keyboard: false,
      })
      .result.finally(() => {
        // ← Refresh file statuses after modal closes
        this.loadDetails();
      });
  }

  // ── Manual close via X button ──────────────────────────────
  closeView(): void {
    this.modalService.dismissAll();
    this.loadDetails();
  }

  // ── Sorting ────────────────────────────────────────────────
  sortFiles(column: keyof BulkFileDetail): void {
    if (column === this.currentSortColumn) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortColumn = column as string;
      this.sortDirection = 'asc';
    }
    this.fileDetails.sort((a, b) => {
      if (a[column] < b[column]) return this.sortDirection === 'asc' ? -1 : 1;
      if (a[column] > b[column]) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // ── Helpers ────────────────────────────────────────────────
  getStatusLabel(status: string): string {
    const map: Record<string, string> = {
      PENDING: 'Pending',
      AWAITING_APPROVAL: 'Awaiting Approval',
      COMPLETED: 'Completed',
      FAILED: 'Failed',
      IN_PROGRESS: 'In Progress',
      CANCELLED: 'Cancelled',
    };
    return map[status] ?? status;
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      PENDING: 'otherStatusColor',
      AWAITING_APPROVAL: 'otherStatusColor',
      COMPLETED: 'compltdStatusColor',
      FAILED: 'errorStatusColor',
      IN_PROGRESS: 'otherStatusColor',
      CANCELLED: 'otherStatusColor',
    };
    return map[status] ?? 'otherStatusColor';
  }
  getsendPdf(){
    this.orderService.getBulkOrderPdfData(this.group?.bulk_order_id).subscribe(
      (res: any) => {
        if (res.status === 200) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail:
              res.message ||
              'Your report is being processed. You will receive it via email shortly.',
            life: 5000,
          });
        } else {
          this.messageService.add({
            severity: 'info',
            summary: 'Information',
            detail: res.message || 'Your request is being processed',
            life: 5000,
          });
        }
      },
      (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Failed to process your request',
          life: 5000,
        });
      }
    );
  }
}